import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { DestroyRef, inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { __ } from '@app/shared/functions/object.functions';
import { ApplicationUser } from '@app/shared/models/classes/ApplicationUser';
import { Role } from '@app/shared/models/classes/Role';
import { Tenant } from '@app/shared/models/classes/Tenant';
import { ApiUrlService } from '@app/shared/services/api-url.service';
import { ITenantService } from '@app/shared/services/itenant.service';
import { environment } from '@env/environment';
import { OAuthService } from 'angular-oauth2-oidc';
import { from, Observable, Subject, Subscriber } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { HypecastOAuthConfig } from '../models/hypecast-oauth-config';
import { HYPECAST_OAUTH_CONFIG } from '../tokens/hypecast-oauth-config.token';

const credentialsKey = 'hypecast-credentials';

export class HypecastCredentials {
  user!: ApplicationUser;
  data!: {
    [key: string]: number | string | boolean;
  };
}

/**
 * Provides a base for authentication workflow.
 */
@Service({ autoProvided: false })
export class HypecastAuthenticationService {
  private oAuthService = inject(OAuthService);
  private oAuthConfig = inject<HypecastOAuthConfig>(HYPECAST_OAUTH_CONFIG);
  private tenantService = inject(ITenantService);
  private apiUrlService = inject(ApiUrlService);
  private destroyRef = inject(DestroyRef);

  private _credentials!: HypecastCredentials | null;

  get credentials(): HypecastCredentials | null {
    return this._credentials;
  }

  private _credentials$: Subject<HypecastCredentials> = new Subject<HypecastCredentials>();

  credentials$: Observable<HypecastCredentials>;

  private readonly httpClient: HttpClient;

  /**
   * Whether the stored credentials belong to the user the CURRENT access token
   * authenticates. Guards against the "UI shows a previous admin while API calls run
   * as the new one" bug: when a different admin logs in (or the token is refreshed for
   * another identity) while a stale `hypecast-credentials` blob is still in
   * localStorage, the stored user must NOT be trusted. The id_token `sub` claim is the
   * authoritative identity for the active token.
   */
  private storedCredentialsMatchToken(stored: HypecastCredentials | null): boolean {
    if (__.IsNullOrUndefined(stored) || __.IsNullOrUndefined(stored.user)) {
      return false;
    }
    const claims = this.oAuthService.getIdentityClaims() as { sub?: string } | null;
    const tokenUserId = claims?.sub;
    return !__.IsNullOrUndefinedOrEmpty(tokenUserId) && stored.user.id === tokenUserId;
  }

  constructor() {
    const httpBackend = inject(HttpBackend);

    // /users/me bootstraps authentication itself, so it must bypass the
    // application interceptor chain and attach the access token explicitly.
    this.httpClient = new HttpClient(httpBackend);

    this.credentials$ = new Observable<HypecastCredentials>((subscriber: Subscriber<HypecastCredentials>) => {
      if (this.oAuthService.hasValidAccessToken()) {
        // User has a valid access token

        if (!__.IsNullOrUndefined(this._credentials)) {
          // If the credentials have already been set
          subscriber.next(this._credentials);
          return undefined;
        } else {
          // No credentials have been set

          let item: HypecastCredentials | null = null;
          try {
            item = JSON.parse(localStorage.getItem(credentialsKey)!) as HypecastCredentials;
          } catch {
            localStorage.removeItem(credentialsKey);
          }

          if (this.storedCredentialsMatchToken(item)) {
            // The stored credentials belong to the current token's user — safe fast path.
            this._credentials = item;
            subscriber.next(this._credentials!);
          } else {
            // No (valid) stored credentials for this token — drop any stale blob from a
            // previous user and wait for init()/`/users/me` to resolve the real user.
            if (!__.IsNullOrUndefined(item)) {
              localStorage.removeItem(credentialsKey);
              sessionStorage.removeItem(credentialsKey);
            }
            return this._credentials$.subscribe({
              next: (credentials: HypecastCredentials) => {
                subscriber.next(credentials);
              },
              error: (error: HttpErrorResponse) => {
                subscriber.error(error);
              },
            });
          }
        }

        return undefined;
      }

      // No valid access token exists
      return this._credentials$.subscribe({
        next: (credentials: HypecastCredentials) => {
          subscriber.next(credentials);
        },
        error: (error: HttpErrorResponse) => {
          subscriber.error(error);
        },
      });
    });
  }

  checkIfAdminOrLogOut(roles: string[]) {
    if (roles.findIndex((q) => q.includes('Admin')) === -1) {
      this.tenantService.resetTenant();

      sessionStorage.removeItem(credentialsKey);
      localStorage.removeItem(credentialsKey);

      // Deliberate logout: re-enable redirects so logOut() can reach the end-session endpoint
      this.oAuthConfig.allowLoginRedirects();
      this.oAuthService.logOut({ missingPermissions: true });

      return true;
    }
    return false;
  }

  init(): Observable<HypecastCredentials> {
    if (!this.oAuthService.hasValidAccessToken()) {
      return new Observable((s) => {
        s.error({ error: null, message: 'Cannot get user and permissions without valid access token' });
      });
    }

    from(this.tenantService.loadTenant())
      .pipe(
        switchMap((_tenant: Tenant) => {
          return this.getCurrentUser();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (data: ApplicationUser) => {
          // `data` is /users/me for the CURRENT access token — the authoritative
          // identity. Gate on its roles, then always rebuild credentials from it so the
          // UI can never show a previous admin while the token belongs to another.
          if (!__.IsNullOrUndefinedOrEmpty(data) && this.checkIfAdminOrLogOut(data.roles)) {
            return;
          }

          if (!__.IsNullOrUndefined(this._credentials) && this._credentials.user?.id === data.id) {
            // Credentials already set for the current user.
            this._credentials$.next(this._credentials);
            return;
          }

          const credentials = Object.assign(new HypecastCredentials(), {
            user: data,
            data: this.oAuthService.getIdentityClaims(),
          } as HypecastCredentials);

          this.saveToStorage(credentials);
          this._credentials = credentials;
          this._credentials$.next(credentials);
        },
        error: (error: HttpErrorResponse) => {
          this._credentials$.error(error);
        },
      });

    return this.credentials$;
  }

  get user(): ApplicationUser {
    return this.credentials!.user;
  }

  hasRole(group: string) {
    if (__.IsNullOrUndefinedOrEmpty(group)) {
      return true;
    }
    if (environment.testPermissions === false && environment.production !== true) {
      return true;
    }
    if (!__.IsNullOrUndefined(this.credentials?.user?.roles)) {
      if (this.isAdministrator()) {
        return true;
      }
      return this.credentials?.user?.roles.some((q) => q === group);
    }
    return false;
  }

  hasAnyRole(groups: string[]) {
    if (groups.length === 0) {
      return true;
    }
    if (environment.testPermissions === false && environment.production !== true) {
      return true;
    }
    if (!__.IsNullOrUndefined(this.credentials?.user?.roles)) {
      if (this.isAdministrator()) {
        return true;
      }

      return groups.findIndex((a) => this.credentials?.user?.roles.some((q) => q === a)) > -1;
    }
    return false;
  }

  isAdministrator(): boolean {
    const roles = this.credentials?.user?.roles ?? [];
    return roles.indexOf(Role.Administrator) > -1 || roles.indexOf(Role.CompanyAdministrator) > -1;
  }

  isCompanyAdministrator(): boolean {
    return (this.credentials?.user?.roles ?? []).indexOf(Role.CompanyAdministrator) > -1;
  }

  isAuthenticated(): boolean {
    return this.oAuthService.hasValidAccessToken();
  }

  getAccessToken(): string {
    return this.oAuthService.getAccessToken();
  }

  logout(): void {
    // Clear the in-memory copy too, so a same-tab re-login can't observe the previous user.
    this._credentials = null;
    sessionStorage.removeItem(credentialsKey);
    localStorage.removeItem(credentialsKey);
    this.tenantService.resetTenant();
    return this.oAuthService.logOut();
  }

  private saveToStorage(credentials: HypecastCredentials): void {
    this._credentials = credentials || null;

    if (!__.IsNullOrUndefined(credentials)) {
      const storage = localStorage;
      storage.setItem(credentialsKey, JSON.stringify(credentials));
    } else {
      sessionStorage.removeItem(credentialsKey);
      localStorage.removeItem(credentialsKey);
    }
  }

  private getCurrentUser(): Observable<ApplicationUser> {
    let headers = new HttpHeaders();
    const token = this.oAuthService.getAccessToken();

    headers = headers.set('Authorization', `Bearer ${token}`);

    return this.httpClient
      .get<{ data: ApplicationUser }>(`${this.apiUrlService.getApiUrl()}api/v1/users/me`, { headers })
      .pipe(
        map((result: { data: ApplicationUser }) => {
          return result.data;
        }),
      );
  }
}
